import { Badge, BadgeProps } from "@mui/material";
import React, { ElementType, forwardRef, ReactElement } from "react";
import { MdsIcon, MdsIconProps } from "../MdsIcon/MdsIcon";

export interface IconBadgedProps extends MdsIconProps {
  /**
   * Icon rendered inside the badge.
   */
  badgeIcon: ElementType;
  /**
   * slotProps forwarded to the underlying MUI Badge. Merged on top of the component defaults.
   */
  slotProps?: BadgeProps["slotProps"];

  badgeIconOverlay?: "normal" | "small";
}

/**
 * Renders an MdsIcon decorated with a small circular MUI Badge containing a second MdsIcon.
 * All MdsIconProps (other than icon / badgeIcon / slotProps) propagate to both inner icons.
 */
export const IconBadged = forwardRef<SVGSVGElement, IconBadgedProps>(function IconBadged(
  { icon, badgeIcon, slotProps, badgeIconOverlay = "normal", ...iconProps },
  ref
): ReactElement {
  return (
    <Badge
      anchorOrigin={{ vertical: "bottom" }}
      badgeContent={<MdsIcon icon={badgeIcon} {...iconProps} fontSize="xs" />}
      color="primary"
      slotProps={{
        badge: {
          sx: (theme) => ({
            right: "4px",
            bottom: "4px",
            color: theme.palette.text.primary,
            backgroundColor: "transparent",
            padding: 0,
            borderRadius: "50% !important",
            minWidth: "16px",
            height: "16px",
          }),
        },
        ...slotProps,
      }}
    >
      <MdsIcon
        icon={icon}
        {...iconProps}
        ref={ref}
        sx={{
          mask:
            badgeIconOverlay === "small"
              ? "radial-gradient(circle at calc(100% - 4px) calc(100% - 4px), transparent 6px, black 7px)"
              : "radial-gradient(circle at calc(100% - 4px) calc(100% - 4px), transparent 8px, black 9px)",
          ...iconProps.sx,
        }}
      />
    </Badge>
  );
});
